export type SuggestedBlockKind = 'event' | 'task' | 'routine';

export interface SuggestedBlockPalette {
  /** The card's own background — always white, never tinted. */
  cardBackground: string;
  /** Primary title/body text. */
  primaryText: string;
  /** "Before" (struck-through) text and other de-emphasized text — red in
   * the plain chat-feed context, softened to gray in the grouped "screen"
   * (Plan My Day/SuggestionGroupCard) context. */
  secondaryText: string;
  /** Filled pill background for recurrence/location/calendar selector chips
   * and routine-recurring-day pickers. Event blocks fill these; Task/Routine
   * blocks render the equivalent chip unfilled (transparent). */
  chipBackground: string;
  chipText: string;
  /** Time pill / description-box background — always the tinted gray-purple,
   * regardless of variant. */
  timePillBackground: string;
  /** Time pill text — only overridden (to a softer gray) in the "screen"
   * variant; undefined elsewhere falls back to primaryText. */
  timePillText?: string;
  /** Accent color for the highlighted current-date label. */
  accent: string;
}

/**
 * Mirrors lifestack-calendar's inline `palette = {...}` object defined in
 * each of AssistantChatSuggestedEventBlock.tsx / AssistantChatSuggestedTaskBlock.tsx /
 * AssistantChatSuggestedRoutineBlock.tsx — centralizes the same
 * isScreenVariant-aware color decisions here as one shared helper instead of
 * duplicating slightly-differing hardcoded SCSS per block (which is how the
 * card/pill background inversion — and the always-red "before" text — crept
 * in previously).
 */
export const getSuggestedBlockPalette = (
  kind: SuggestedBlockKind,
  isScreenVariant: boolean
): SuggestedBlockPalette => ({
  cardBackground: 'var(--color-white-lightest, #ffffff)',
  primaryText: 'var(--color-black, #131314)',
  secondaryText: isScreenVariant
    ? 'var(--color-gray-dark, #7c7c7c)'
    : 'var(--color-red-base, #f34720)',
  // RN: Event blocks fill selector chips with the tinted background; Task
  // and Routine blocks render theirs unfilled.
  chipBackground:
    kind === 'event' ? 'var(--color-gray-purple, #f9f8fc)' : 'transparent',
  chipText: 'var(--color-black, #131314)',
  timePillBackground: 'var(--color-gray-purple, #f9f8fc)',
  timePillText: isScreenVariant ? 'var(--color-gray-dark, #7c7c7c)' : undefined,
  accent: isScreenVariant ? 'var(--color-black, #131314)' : 'var(--color-primary, #680ddb)',
});
